<template>
    <div class="capability-tab" v-loading="loading">
        <div v-if="!productModel" class="empty">请先在基础信息中创建型号</div>
        <template v-else>
            <div class="toolbar">
                <div class="copy-from">
                    <span class="row-label">从其他型号复制</span>
                    <el-select v-model="copySource" filterable remote clearable size="small" style="width: 260px;"
                        placeholder="输入型号搜索" :remote-method="searchModels" :loading="searching"
                        no-data-text="没有其他型号" @visible-change="(v) => v && !modelOptions.length && searchModels('')">
                        <el-option v-for="m in modelOptions" :key="m.model" :label="m.model" :value="m.model"></el-option>
                    </el-select>
                    <el-button size="small" plain :disabled="!copySource" @click="copyFrom">复制</el-button>
                </div>
                <div>
                    <el-tag v-if="!configured" size="small" type="info">未配置，App 使用内置表</el-tag>
                    <el-button size="small" icon="el-icon-view" :disabled="!configured" @click="openPreview">下发预览</el-button>
                    <el-button size="small" @click="load">重置</el-button>
                    <el-button type="primary" size="small" :loading="saving" @click="save">保存</el-button>
                </div>
            </div>

            <div class="derived">
                <span class="row-label">由基础信息决定</span>
                <el-tag size="small" :type="isLowPower ? 'warning' : 'info'">is_low_power：{{ isLowPower }}</el-tag>
                <el-tag size="small" :type="is4G ? 'warning' : 'info'">support_4g：{{ is4G }}</el-tag>
                <span class="form-tip">分别取自「产品类型 = 低功耗设备」「网络类型 = 4G」，在基础信息中修改</span>
            </div>

            <div v-for="group in groups" :key="group.name" class="cap-group">
                <div class="group-title">{{ group.name }}</div>
                <div class="cap-list">
                    <div v-for="f in group.fields" :key="f.code" class="cap-item">
                        <el-checkbox v-model="form.capabilities[f.code]">{{ f.name }}</el-checkbox>
                        <div class="cap-meta">
                            <code>{{ f.code }}</code>
                            <span>{{ f.usage }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="cap-group">
                <div class="group-title">其他</div>
                <el-form label-width="130px" size="small" class="other-form">
                    <el-form-item label="连接策略">
                        <el-radio-group v-model="form.connect_strategy">
                            <el-radio :label="null">不下发</el-radio>
                            <el-radio :label="0">0</el-radio>
                            <el-radio :label="1">1</el-radio>
                        </el-radio-group>
                        <div class="form-tip">connect_strategy，WebRTC 连接方式，仅 Android 使用。0 = 长连接：进入设备页就预连接并自动重连，打开直播更快；1 = 短连接：只在播放时连接，不预连、不在后台重连，适合低功耗设备省电。不下发时 Android 默认 GW5 为 1，其余为 0</div>
                    </el-form-item>
                    <el-form-item label="modeType">
                        <el-input-number v-model="form.mode_type" :min="0" :controls="false" placeholder="不下发"
                            style="width: 120px;"></el-input-number>
                        <el-button v-if="form.mode_type !== null && form.mode_type !== undefined" type="text" size="small"
                            @click="form.mode_type = null">清空</el-button>
                        <div class="form-tip">Android 播放器读取，目前只有 W1-CAM 配置为 10；留空不下发</div>
                    </el-form-item>
                    <el-form-item label="设备图">
                        <div class="pair-image-row">
                            <el-input v-model.trim="form.pair_image" clearable placeholder="留空使用配网型号的配网图，再没有用配网方案主图"></el-input>
                            <el-image v-if="form.pair_image" :src="form.pair_image" fit="contain" class="pair-image-thumb"
                                :preview-src-list="[form.pair_image]"></el-image>
                        </div>
                        <div class="form-tip">pair_image，免费云存、播放器等处的设备图</div>
                    </el-form-item>
                </el-form>
            </div>
        </template>

        <el-dialog :title="`下发预览：${productModel}`" v-model="previewVisible" width="640px" append-to-body>
            <div class="form-tip" style="margin-bottom: 8px;">
                按已保存的配置生成，结构与原 osaio_IPCFunctionTable.json 中的一条一致；未保存的修改不会出现在这里。<br>
                pair_image 依次取：设备图 → 配网型号的配网图 → 第一个配网方案的产品主图，都没有时不输出；连接策略、modeType 不下发时也没有对应字段，与原表一致
            </div>
            <div v-if="!previewData" class="empty">还没有所属配网型号，不会下发。设备能力表里的 model、name、modelId、设备图取自所属配网型号，请先在「配网」tab 选择</div>
            <pre v-else class="preview-json">{{ previewData }}</pre>
        </el-dialog>
    </div>
</template>

<script>
import pairingApi from './api'

export default {
    name: 'CapabilityTab',
    props: {
        // 已保存的内部型号，即设备上报的完整类型，如 GC2_A10S8A5
        productModel: { type: String, default: '' },
        // 基础信息：产品类型 2=低功耗设备；网络类型 2=4G
        modelType: { type: [Number, String], default: '' },
        networkType: { type: [Number, String], default: '' },
    },
    data() {
        return {
            fields: [],
            form: this.emptyForm(),
            configured: false,
            loading: false,
            saving: false,
            copySource: '',
            previewVisible: false,
            previewData: '',
            modelOptions: [],
            searching: false,
        }
    },
    computed: {
        isLowPower() {
            return Number(this.modelType) === 2
        },
        is4G() {
            return Number(this.networkType) === 2
        },
        groups() {
            const map = {}
            this.fields.forEach((f) => {
                if (!map[f.group]) map[f.group] = { name: f.group, fields: [] }
                map[f.group].fields.push(f)
            })
            return Object.values(map)
        },
    },
    watch: {
        productModel: {
            immediate: true,
            handler(model) {
                if (model) this.load()
            },
        },
    },
    created() {
        pairingApi.PairingMeta().then((res) => {
            this.fields = res.data.data.capability_fields
        })
    },
    methods: {
        emptyForm() {
            return { capabilities: {}, connect_strategy: null, mode_type: null, pair_image: '' }
        },
        fill(data) {
            const form = this.emptyForm()
            if (data) {
                form.capabilities = { ...data.capabilities }
                form.connect_strategy = data.connect_strategy === undefined ? null : data.connect_strategy
                form.mode_type = data.mode_type === undefined ? null : data.mode_type
                form.pair_image = data.pair_image || ''
            }
            this.form = form
        },
        load() {
            this.loading = true
            pairingApi.CapabilityDetail({ product_model: this.productModel }).then((res) => {
                this.loading = false
                this.configured = !!res.data.data
                this.fill(res.data.data)
            }).catch(() => {
                this.loading = false
            })
        },
        openPreview() {
            pairingApi.AppFunctionTable({ productModel: this.productModel }).then((res) => {
                const item = res.data.data[this.productModel]
                this.previewData = item ? JSON.stringify({ [this.productModel]: item }, null, 2) : ''
                this.previewVisible = true
            })
        },
        searchModels(keyword) {
            this.searching = true
            this.api.ProductModelList({ page: 1, limit: 20, model: keyword || '' }).then((res) => {
                this.searching = false
                const list = res.data.code == 200 ? res.data.data.data || [] : []
                this.modelOptions = list.filter((m) => m.model && m.model !== this.productModel)
            }).catch(() => {
                this.searching = false
            })
        },
        // 复制到表单，保存后才生效；设备图不复制，避免误用其他型号的图片
        copyFrom() {
            pairingApi.CapabilityDetail({ product_model: this.copySource }).then((res) => {
                if (!res.data.data) {
                    this.$message.warning('该型号还没有配置设备能力')
                    return
                }
                const pairImage = this.form.pair_image
                this.fill(res.data.data)
                this.form.pair_image = pairImage
                this.$message.success('已复制，保存后生效')
            })
        },
        save() {
            this.saving = true
            // 未勾选的开关显式存 false，保证下发时每个字段都有值
            const capabilities = {}
            this.fields.forEach((f) => {
                capabilities[f.code] = !!this.form.capabilities[f.code]
            })
            pairingApi.CapabilitySave({
                product_model: this.productModel,
                capabilities,
                connect_strategy: this.form.connect_strategy,
                mode_type: this.form.mode_type === undefined ? null : this.form.mode_type,
                pair_image: this.form.pair_image,
            }).then((res) => {
                this.saving = false
                if (res.data.code == 200) {
                    this.$message.success('保存成功')
                    this.configured = true
                    return
                }
                this.$message.error('保存失败' + res.data.msg)
            })
        },
    },
}
</script>

<style scoped>
.toolbar,
.copy-from,
.derived {
    display: flex;
    align-items: center;
    gap: 10px;
}

.toolbar {
    justify-content: space-between;
    margin-bottom: 12px;
}

.toolbar > div:last-child {
    display: flex;
    align-items: center;
    gap: 8px;
}

.derived {
    margin-bottom: 12px;
    padding: 8px 12px;
    background: #f5f7fa;
    border-radius: 4px;
}

.row-label {
    font-size: 13px;
    color: #606266;
}

.form-tip {
    font-size: 12px;
    line-height: 18px;
    color: #909399;
}

.cap-group {
    margin-bottom: 16px;
}

.group-title {
    margin-bottom: 8px;
    padding-left: 8px;
    border-left: 3px solid #409eff;
    font-size: 14px;
    font-weight: 500;
    line-height: 16px;
    color: #303133;
}

.cap-list {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px 16px;
}

.cap-item {
    padding: 6px 10px;
    border: 1px solid #ebeef5;
    border-radius: 4px;
}

.cap-meta {
    margin-top: 2px;
    padding-left: 22px;
    font-size: 12px;
    line-height: 18px;
    color: #909399;
}

.cap-meta code {
    margin-right: 6px;
    color: #606266;
}

.other-form {
    max-width: 760px;
}

.pair-image-row {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
}

.pair-image-thumb {
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    border: 1px solid #e4e7ed;
    border-radius: 3px;
}

.preview-json {
    max-height: 60vh;
    margin: 0;
    padding: 10px;
    overflow: auto;
    font-size: 12px;
    line-height: 18px;
    background: #f5f7fa;
    border-radius: 4px;
}

.empty {
    padding: 40px 0;
    text-align: center;
    font-size: 13px;
    color: #c0c4cc;
}
</style>
